/**
 * ============================================================================
 * [P3-01] CharacterCounter - 글자 수 카운터 컴포넌트
 *
 * 기능:
 * - 현재 글자 수 / 최대 글자 수 표시
 * - 90% 이상: 주황색 경고
 * - 100% 초과: 빨간색 경고
 * - 예상 읽기 시간 표시 (선택)
 *
 * [2026-01-31] Phase 3 구현
 * ============================================================================
 */

// ============================================================================
// 1. Imports
// ============================================================================
import { useMemo } from 'react'
import styles from './ScriptEditor.module.css'

// ============================================================================
// 2. Types
// ============================================================================
interface CharacterCounterProps {
  current: number
  max?: number                   // 기본값: 500
  showTimeEstimate?: boolean     // 기본값: true
}

// ============================================================================
// 3. CharacterCounter Component
// ============================================================================
export function CharacterCounter({
  current,
  max = 500,
  showTimeEstimate = true,
}: CharacterCounterProps) {
  // ==========================================================================
  // 3-1. 상태 계산
  // ==========================================================================
  const status = useMemo(() => {
    const ratio = current / max
    if (ratio > 1) return 'over'        // 초과
    if (ratio >= 0.9) return 'warning'  // 90% 이상
    return 'normal'
  }, [current, max])

  // ==========================================================================
  // 3-2. 예상 읽기 시간 (500자 = 1분 기준)
  // ==========================================================================
  const timeEstimate = useMemo(() => {
    const minutes = Math.ceil(current / 500)
    return `약 ${minutes}분`
  }, [current])

  // ==========================================================================
  // 3-3. 스타일 클래스 결정
  // ==========================================================================
  const statusClass = useMemo(() => {
    switch (status) {
      case 'over':
        return styles.counterOver
      case 'warning':
        return styles.counterWarning
      default:
        return styles.counterNormal
    }
  }, [status])

  // ==========================================================================
  // 3-4. Render
  // ==========================================================================
  return (
    <div className={`${styles.characterCounter} ${statusClass}`}>
      <span className={styles.counterText}>
        ({current} / {max}자
        {showTimeEstimate && `는 ${timeEstimate}`})
      </span>
    </div>
  )
}

export default CharacterCounter
